import React, { useRef } from "react";
import { Button } from "react-bootstrap";
import toast from "react-hot-toast";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useContext } from "react";
import indianCities from "../../../components/Data/IndianCities";
import Select from "react-select";

import { useAtom } from "jotai";
import { gbState } from "../../../components/shared/GlobalState";

//Context import
import AuthContext from "../../../components/shared/Context";

const CreateTCChild = () => {
  const { checkLoggedInStatus, loggedInUserDetails } = useContext(AuthContext);
  let navigate = useNavigate();
  const districtRef = useRef();
  const stateInputRef = useRef();

  //Error Logging Service & Santize Input
  const [{ errorLogApi, sanitizeInput }] = useAtom(gbState);

  //To check if the user is logged in
  useEffect(() => {
    checkLoggedInStatus();
  }, []);

  const [stateValue, setStateValue] = useState(null);
  const [city, setCity] = useState(null);

  const [formData, setFormData] = useState({
    name_tc: null,
    hdcc_no: null,
    institution: null,
    primary_email: null,
    secondary_email: null,
    phone: null,
    fax: null,
    website: null,
    country: null,
    state: null,
    district: null,
    pincode: null,
    address_line1: null,
    address_line2: null,
    landmark: null,
    tc_type: "government",
  });

  //Created a set for indian cities
  const statesSet = new Set(
    indianCities.map((city, index) => {
      return city.state;
    })
  );

  //Converted Set to Array of States
  const states = [...statesSet];

  useEffect(() => {
    if (loggedInUserDetails.role === "ST_ADM_ACCESS") {
      setFormData({ ...formData, state: loggedInUserDetails.state });
      setStateValue(loggedInUserDetails.state);
    }
  }, []);

  const handleTCRegistration = (e) => {
    e.preventDefault();

    // Validate required fields with friendly messages
    if (!formData.name_tc?.trim()) {
      toast.error("Please enter the Treatment Center name");
      return;
    }

    if (!formData.institution?.trim()) {
      toast.error("Please enter the Institution name");
      return;
    }

    if (!formData.hdcc_no?.trim()) {
      toast.error("Please enter the HDCC number");
      return;
    }

    if (!formData.primary_email?.trim()) {
      toast.error("Please enter the primary email address");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.primary_email)) {
      toast.error("Please enter a valid primary email address");
      return;
    }

    if (
      formData.secondary_email &&
      !emailRegex.test(formData.secondary_email)
    ) {
      toast.error("Please enter a valid secondary email address");
      return;
    }

    if (!formData.phone?.trim()) {
      toast.error("Please enter the phone number");
      return;
    }

    // Validate phone number (assuming Indian format)
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(formData.phone)) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    if (!formData.state) {
      toast.error("Please select a state");
      stateInputRef.current.focus();
      return;
    }

    if (!formData.district) {
      toast.error("Please select a district");
      districtRef.current.focus();
      return;
    }

    // Validate pincode if provided
    if (formData.pincode && !/^\d{6}$/.test(formData.pincode)) {
      toast.error("Please enter a valid 6-digit pincode");
      return;
    }

    let userToken = localStorage.getItem("token");
    const config = {
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/tc/add`,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
      data: { ...formData, country: "India" },
    };

    axios(config)
      .then(function (response) {
        toast.success("Treatment Center registered successfully!");
        navigate("/tcenters");
      })
      .catch(function (error) {
        // More user-friendly error messages
        if (error.response?.data?.errors) {
          // Handle validation errors
          const errorMessages = Object.values(error.response.data.errors)
            .map((err) => err.message)
            .join("\n");
          toast.error(
            errorMessages || "Validation failed. Please check your inputs."
          );
        } else if (error.response?.status === 401) {
          toast.error("Your session has expired. Please login again.");
          navigate("/login", { replace: true });
        } else if (error.response?.status === 403) {
          toast.error("You don't have permission to perform this action.");
        } else if (error.response?.status === 409) {
          toast.error(
            "A Treatment Center with this HDCC number already exists."
          );
        } else {
          toast.error(
            "Unable to register Treatment Center. Please try again later."
          );
        }

        errorLogApi(error, "CreateTCChild", "/tcenters/add");
      });
  };

  const stateOptions = states.map((state) => ({
    value: state,
    label: state,
  }));

  const districtOptions = indianCities
    .filter((state) => {
      return state.state === stateValue;
    })
    .map((state) => ({ value: state.city, label: state.city }));

  const handleChange = ({ currentTarget: input }) => {
    const value = input.value.trim();

    if (sanitizeInput(value, input.name)) {
      // Additional validation for specific fields
      if (input.name === "phone" && value && !/^[6-9]\d{0,9}$/.test(value)) {
        toast.error("Please enter a valid phone number starting with 6-9");
        return;
      }

      if (input.name === "pincode" && value && !/^\d{0,6}$/.test(value)) {
        toast.error("Pincode should be 6 digits");
        return;
      }

      if (input.name === "website" && value && !value.startsWith("http")) {
        toast.error("Website URL should start with http:// or https://");
        return;
      }

      setFormData({
        ...formData,
        [input.name]: value,
      });
    }
  };

  const dropDownChange = (selected, dropdown) => {
    setFormData({
      ...formData,
      [dropdown.name]: selected.value,
    });
  };

  const tcTypeOptions = [
    { value: "government", label: "Government" },
    { value: "private", label: "Private" },
  ];

  return (
    <div className="patient-list-tc">
      <div>
        <h4>Add Treatment Center</h4>
      </div>
      <form onSubmit={(e) => handleTCRegistration(e)}>
        <div className="patientUpdateButton">
          <Button variant="primary" size="medium" type="submit">
            Save TC
          </Button>
          &nbsp;&nbsp;
          <Button
            variant="secondary"
            size="medium"
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </div>
        <div className="newTCFormUsers">
          {/* {JSON.stringify(formData)} */}
          <div className="newPatientItem w-25">
            <label htmlFor="first_name">Name of Treatment Center</label>
            <input
              type="text"
              name="name_tc"
              value={formData.name_tc}
              onChange={handleChange}
              onClick={handleChange}
              required
            />
          </div>
          <div className="newPatientItem w-25">
            <label htmlFor="institution">Institution</label>
            <input
              type="text"
              name="institution"
              value={formData.institution}
              onChange={handleChange}
              onClick={handleChange}
              required
            />
          </div>
          <div className="newPatientItem w-25">
            <label htmlFor="hdcc_no">HDCC No</label>
            <input
              type="text"
              name="hdcc_no"
              value={formData.hdcc_no}
              onChange={handleChange}
              onClick={handleChange}
              required
            />
          </div>
          <div className="newPatientItem w-25">
            <label htmlFor="tc_type">TC Type</label>
            <Select
              name="tc_type"
              onChange={(selected, dropdown) => {
                setFormData({
                  ...formData,
                  tc_type: selected.value,
                });
              }}
              className="newPatientSelect"
              options={tcTypeOptions}
              value={
                formData?.tc_type &&
                tcTypeOptions.find((t) => t.value === formData?.tc_type)
              }
            />
          </div>
          <div className="newPatientItem w-25">
            <label htmlFor="primary_email">Email (Primary)</label>
            <input
              type="email"
              name="primary_email"
              value={formData.primary_email}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  primary_email: e.target.value,
                });
              }}
              required
            />
          </div>
          <div className="newPatientItem w-25">
            <label htmlFor="secondary_email">Email (Secondary)</label>
            <input
              type="email"
              name="secondary_email"
              value={formData.secondary_email}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  secondary_email: e.target.value,
                });
              }}
            />
          </div>
          <div className="newPatientItem w-25">
            <label htmlFor="phone">
              Phone Number <span className="text-muted">(10 digits)</span>
            </label>
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              onClick={handleChange}
              placeholder="Enter 10-digit number"
              required
            />
          </div>
          <div className="newPatientItem w-25">
            <label htmlFor="fax">Fax </label>
            <input
              type="text"
              name="fax"
              value={formData.fax}
              onChange={handleChange}
              onClick={handleChange}
            />
          </div>
          <div className="newPatientItem w-25">
            <label htmlFor="website">Website </label>
            <input
              type="text"
              name="website"
              placeholder="https://"
              value={formData.website}
              onChange={handleChange}
              onClick={handleChange}
            />
          </div>
          <div className="newPatientItem w-25">
            <label htmlFor="country">Country </label>
            <input type="text" name="country" value="India" disabled />
          </div>
          {!(loggedInUserDetails.role === "ST_ADM_ACCESS") && (
            <div className="newPatientItem w-25">
              <label>State</label>
              <Select
                name="state"
                onChange={(selected, dropdown) => {
                  setStateValue(selected.value);
                  setCity(null);
                  dropDownChange(selected, dropdown);
                }}
                className="newPatientSelect"
                options={stateOptions}
                ref={stateInputRef}
              />
            </div>
          )}
          {loggedInUserDetails.role === "ST_ADM_ACCESS" && (
            <div className="newPatientItem w-25">
              <label htmlFor="hdcc_no">State</label>
              <input type="text" name="state" value={formData.state} disabled />
            </div>
          )}
          <div className="newPatientItem w-25">
            <label>District/City</label>
            <Select
              name="district"
              onChange={dropDownChange}
              ref={districtRef}
              className="newPatientSelect"
              options={districtOptions}
              isClearable={true}
            />
          </div>
          <div className="newPatientItem w-25">
            <label htmlFor="pincode">Pincode </label>
            <input
              type="text"
              name="pincode"
              value={formData.pincode}
              onChange={handleChange}
              onClick={handleChange}
            />
          </div>
          <div className="newPatientItem w-25">
            <label htmlFor="address_line1">Address line 1 </label>
            <input
              type="text"
              name="address_line1"
              value={formData.address_line1}
              onChange={handleChange}
              onClick={handleChange}
            />
          </div>
          <div className="newPatientItem w-25">
            <label htmlFor="address_line2">Address line 2 </label>
            <input
              type="text"
              name="address_line2"
              value={formData.address_line2}
              onChange={handleChange}
              onClick={handleChange}
            />
          </div>
          <div className="newPatientItem w-25">
            <label htmlFor="landmark">Landmark </label>
            <input
              type="text"
              name="landmark"
              value={formData.landmark}
              onChange={handleChange}
              onClick={handleChange}
            />
          </div>
          {formData.tc_type === "government" && (
            <>
              <div className="newPatientItem w-25">
                <label htmlFor="nodal_status">Nodal Status</label>
                <div className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    id="nodal_status"
                    name="nodal_status"
                    checked={formData.nodal_status}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="newPatientItem w-25">
                <label htmlFor="allocate_to_tcs">Allocate to TC(s)</label>
                <div className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    id="allocate_to_tcs"
                    name="allocate_to_tcs"
                    checked={formData.allocate_to_tcs}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default CreateTCChild;
